import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Clock, Inbox, X } from 'lucide-react';
import { formatDateTime, formatLongDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { money } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  Modal,
  ModalHeader,
  Textarea,
} from '@/components/ui';

interface Declaration {
  id: string;
  loanId: string;
  loanNumber: string;
  outstanding: string;
  memberName: string;
  membershipNumber: string | null;
  amount: string;
  paymentDate: string;
  paidAt: string | null;
  paymentMethod: string;
  transactionReference: string | null;
  note: string | null;
  status: string;
  createdAt: string;
}

/**
 * Members saying they have paid, waiting on an officer.
 *
 * Sits above the repayment list because it is the only part of that screen
 * with work outstanding — a confirmed repayment is a record, a pending
 * declaration is a task. Hidden entirely when the queue is empty rather than
 * showing an empty state, so it never competes for attention with the ledger
 * beneath it.
 */
export function DeclarationQueue() {
  const [reviewing, setReviewing] = useState<Declaration | null>(null);

  const { data } = useQuery({
    queryKey: ['declarations', 'PENDING'],
    queryFn: () =>
      api.get<{ data: Declaration[]; pendingCount: number }>(
        '/repayments/declarations?status=PENDING&pageSize=25',
      ),
    refetchInterval: 60_000,
  });

  const pending = data?.data ?? [];
  if (pending.length === 0) return null;

  return (
    <>
      <Card className="mb-6 border-warning/30">
        <CardContent className="px-0 pt-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-warning-soft px-5 py-3.5">
            <Inbox size={16} className="text-warning" />
            <h2 className="font-heading font-bold text-warning-soft-foreground">
              Declared by members, awaiting confirmation
            </h2>
            <span className="numeric rounded-full bg-warning px-2 py-0.5 text-xs font-bold text-warning-foreground">
              {data?.pendingCount ?? pending.length}
            </span>
          </div>

          <div className="divide-y divide-border">
            {pending.map((declaration) => (
              <div
                key={declaration.id}
                className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="numeric font-heading text-lg font-bold text-foreground">
                      {money(declaration.amount)}
                    </span>
                    <span className="numeric text-sm text-foreground-secondary">
                      {declaration.loanNumber}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">
                    {declaration.memberName}
                    {declaration.membershipNumber ? ` · ${declaration.membershipNumber}` : ''}
                  </p>
                  <p className="numeric mt-0.5 text-xs text-foreground-secondary">
                    Paid {formatLongDate(new Date(declaration.paymentDate))}
                    {declaration.transactionReference
                      ? ` · ref ${declaration.transactionReference}`
                      : ' · no reference given'}
                  </p>
                  {declaration.note && (
                    <p className="mt-1 text-xs italic text-foreground-secondary">
                      &ldquo;{declaration.note}&rdquo;
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <span className="hidden text-xs text-foreground-secondary sm:inline">
                    {money(declaration.outstanding)} outstanding
                  </span>
                  <Button size="sm" onClick={() => setReviewing(declaration)}>
                    Review
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <p className="flex items-start gap-1.5 border-t border-border px-5 py-3 text-xs leading-relaxed text-foreground-secondary">
            <Clock size={12} className="mt-0.5 shrink-0" />
            Check each reference against the bank statement. Nothing reaches the loan until you
            confirm it.
          </p>
        </CardContent>
      </Card>

      {reviewing && (
        <ReviewModal declaration={reviewing} onClose={() => setReviewing(null)} />
      )}
    </>
  );
}

function ReviewModal({
  declaration,
  onClose,
}: {
  declaration: Declaration;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // Pre-filled with what the member said, because it is usually right — but
  // editable, because the bank is the authority on what actually arrived.
  const [confirmedAmount, setConfirmedAmount] = useState(
    (Number(declaration.amount) / 100).toFixed(2),
  );
  const [reviewNote, setReviewNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (decision: 'CONFIRM' | 'REJECT') =>
      api.post('/repayments/declarations/review', {
        declarationId: declaration.id,
        decision,
        ...(decision === 'CONFIRM' ? { confirmedAmount } : {}),
        ...(reviewNote.trim() ? { reviewNote: reviewNote.trim() } : {}),
      }),
    onSuccess: (_, decision) => {
      toast.success(
        decision === 'CONFIRM'
          ? 'Confirmed. The repayment is recorded against the loan.'
          : 'Rejected. The member has been told.',
      );
      for (const key of [['declarations'], ['repayments'], ['admin-overview'], ['loans']]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not record that decision.'),
  });

  const differs = Math.abs(Number(confirmedAmount) * 100 - Number(declaration.amount)) > 0.5;

  return (
    <Modal open onClose={onClose}>
      <ModalHeader
        title="Confirm this payment?"
        description={`${declaration.memberName} · ${declaration.loanNumber}`}
        onClose={onClose}
      />

      <div className="space-y-5 p-6">
        {error && <Alert tone="danger">{error}</Alert>}

        <dl className="space-y-2 rounded-xl bg-muted/50 p-4 text-sm">
          <Row term="Member says they paid" detail={money(declaration.amount)} />
          <Row
            term="On"
            detail={
              declaration.paidAt
                ? formatDateTime(new Date(declaration.paidAt))
                : formatLongDate(new Date(declaration.paymentDate))
            }
          />
          <Row term="Declared" detail={formatDateTime(new Date(declaration.createdAt))} />
          <Row
            term="Bank reference"
            detail={declaration.transactionReference ?? 'None given'}
            emphasise
          />
          <Row term="Outstanding on the loan" detail={money(declaration.outstanding)} />
        </dl>

        <Field
          label="Amount to record"
          htmlFor="confirmedAmount"
          hint="In naira. Change it if the bank shows something different."
          required
        >
          <Input
            id="confirmedAmount"
            className="numeric"
            value={confirmedAmount}
            onChange={(e) => setConfirmedAmount(e.target.value)}
          />
        </Field>

        {differs && (
          <Alert tone="warning">
            This differs from what the member declared. The amount you enter here is what goes on
            the loan.
          </Alert>
        )}

        <Field
          label="Note"
          htmlFor="reviewNote"
          hint="Required when rejecting. The member sees it."
        >
          <Textarea
            id="reviewNote"
            rows={2}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            placeholder="e.g. Matched against the statement of 10 September."
          />
        </Field>
      </div>

      <div className="flex flex-wrap justify-end gap-2 border-t border-border p-4">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          loading={review.isPending && review.variables === 'REJECT'}
          disabled={reviewNote.trim().length < 3}
          onClick={() => review.mutate('REJECT')}
        >
          <X size={15} /> Not found
        </Button>
        <Button
          loading={review.isPending && review.variables === 'CONFIRM'}
          onClick={() => review.mutate('CONFIRM')}
        >
          <Check size={15} /> Confirm and record
        </Button>
      </div>
    </Modal>
  );
}

function Row({
  term,
  detail,
  emphasise,
}: {
  term: string;
  detail: string;
  emphasise?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-foreground-secondary">{term}</dt>
      <dd
        className={
          emphasise
            ? 'numeric font-bold text-foreground'
            : 'numeric font-semibold text-foreground'
        }
      >
        {detail}
      </dd>
    </div>
  );
}
